import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Header } from '../components/layout';
import {
  BankingWidget,
  CreditCardsWidget,
  InvestmentsWidget,
  CryptoWidget,
  Retirement401kWidget,
  PrivateEquityWidget,
  HSAWidget,
  CreditScoresWidget,
  AssetsWidget,
  LiabilitiesWidget,
  ChartsWidget,
  BudgetWidget,
  GoalsWidget,
  FearGreedWidget,
  MarketSnapshotWidget,
  CompoundInterestCalculator,
  DebtPayoffCalculator,
  RetirementCalculator,
  FIRECalculator,
  MilestonesWidget,
  RetirementProjection,
} from '../components/widgets';
import { AddEntityModal, CSVImportModal, UpgradeModal, SettingsModal, PDFExportModal } from '../components/modals';
import type { PDFExportSections } from '../components/modals/PDFExportModal';
import { LoadingBar, Toast } from '../components/shared';
import { StreakCelebration } from '../components/streak/StreakCelebration';
import { ConsentBanner } from '../components/shared/ConsentBanner';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useFinancialData } from '../contexts/FinancialDataContext';
import { useGoogleSheets } from '../hooks/useGoogleSheets';
import { formatCurrency, formatNumber } from '../utils/formatters';
import { calculateReturn, calculatePortfolioGain, calculateLineChartPoints } from '../utils/calculations';
import { useFinancialCalculations } from '../hooks/useFinancialCalculations';
import { HISTORY_LINE_COLORS, STORAGE_KEYS } from '../config/constants';
import { hasUnseenUpdates } from '../config/changelog';
import { TutorialTour } from '../components/shared/TutorialTour';
import { LayoutDashboard, Landmark, TrendingUp, Target, Home, BarChart3, Compass, Calculator, PanelLeftClose, PanelLeft, ArrowUpRight, ArrowDownRight, Minus, Calendar, Plus, ChevronLeft } from 'lucide-react';
import type { DashboardTab, ChartTab, EntityKey } from '../types';
import { supabase } from '../services/supabaseClient';

const DASHBOARD_TABS: Array<{ key: DashboardTab; label: string; icon: React.ElementType }> = [
  { key: 'overview', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'accounts', label: 'Accounts', icon: Landmark },
  { key: 'investments', label: 'Investments', icon: TrendingUp },
  { key: 'charts', label: 'Charts', icon: BarChart3 },
  { key: 'assetsDebts', label: 'Assets & Debts', icon: Home },
  { key: 'budgetGoals', label: 'Budget & Goals', icon: Target },
  { key: 'retirement', label: 'Retirement', icon: Compass },
  { key: 'calculators', label: 'Calculators', icon: Calculator },
];

const TAB_STORAGE_KEY = 'tallyup_dashboard_tab';

export const DashboardPage: React.FC = () => {
  const { darkMode, privacyMode } = useTheme();
  const { error, setError, sheetId, paymentTier, stripeReturnStatus, clearStripeReturnStatus, user, userProfile, googleTokenFailed, reconnectGoogle } = useAuth();
  const {
    bankingData,
    creditCards,
    investments,
    retirement401k,
    assets,
    liabilities,
    totalBanking,
    totalCreditUsed,
    totalCreditLimit,
    totalInvestmentValue,
    total401k,
    totalCrypto,
    totalPrivateEquity,
    privateEquityHoldings,
    cryptoHoldings,
    hsaAccounts,
    totalHSA,
    totalAssets,
    totalLiabilities,
    netWorth,
    handleAddInvestment,
    handleAddBankAccount,
    handleAddCreditCard,
    handleAddAsset,
    handleAddHSA,
    handleAddCreditScore,
    handleAddLiability,
    handleAdd401kHolding,
    handleAddCrypto,
    handleAddPrivateEquity,
    handleBatchAdd401kHoldings,
    handleBatchAddInvestments,
    handleAddBudgetCategory,
    handleAddGoal,
    budgetCategories,
    goals,
    totalBudgeted,
    totalSpent,
    totalGoalsSaved,
    totalGoalsTarget,
    getVisibleNetWorthHistory,
    userSettings,
    isLoading,
  } = useFinancialData();
  // Free sees the last 12 months of history; Pro sees all of it.
  const netWorthHistory = useMemo(
    () => getVisibleNetWorthHistory(paymentTier),
    [getVisibleNetWorthHistory, paymentTier]
  );
  const { refreshData, isRefreshing } = useGoogleSheets();
  const { chartData, budgetUtilization, goalProgress } = useFinancialCalculations();

  // Auto-refresh when user returns to the tab (e.g. after switching away)
  useEffect(() => {
    let lastRefreshTime = Date.now();
    const COOLDOWN_MS = 2 * 60 * 1000; // 2 minutes

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRefreshTime >= COOLDOWN_MS) {
        lastRefreshTime = Date.now();
        refreshData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [refreshData]);

  // Modal states
  const [activeModal, setActiveModal] = useState<EntityKey | null>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  // True when the upgrade screen was opened because the user chose Pro on
  // the pricing page, rather than by hitting a limit.
  const [upgradeChosePro, setUpgradeChosePro] = useState(false);
  const [upgradeModalType, setUpgradeModalType] = useState<'investments' | 'bankAccounts' | 'retirement401kHoldings' | 'privateEquityHoldings' | 'cryptoHoldings' | 'general'>('investments');
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [showInvestmentCSVImport, setShowInvestmentCSVImport] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPDFExportModal, setShowPDFExportModal] = useState(false);
  const [hasNewUpdates, setHasNewUpdates] = useState(() => hasUnseenUpdates());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [runTutorial, setRunTutorial] = useState(() => {
    return !localStorage.getItem(STORAGE_KEYS.TUTORIAL_COMPLETED);
  });

  // Dashboard tab state (persisted to localStorage)
  const [activeTab, setActiveTab] = useState<DashboardTab>('overview');

  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('tallyup_sidebar_collapsed') === 'true';
  });

  const [chartsDefaultTab, setChartsDefaultTab] = useState<ChartTab | undefined>(undefined);

  // Mobile bottom-nav: scroll-shrink + carousel paging
  const { scrollY } = useScroll();
  const bottomNavScale = useTransform(scrollY, [0, 120], [1, 0.92], { clamp: true });
  const bottomNavY = useTransform(scrollY, [0, 120], [0, 2], { clamp: true });
  // Lift the pill above iOS Safari's bottom URL-bar reveal zone (~80px) when
  // the URL bar is hidden, so taps register on the first try instead of just
  // re-showing the toolbar. When the URL bar is visible the visual viewport
  // is shorter than the window, so we can stay snug to it.
  const [bottomNavInsetPx, setBottomNavInsetPx] = useState(4);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    const update = () => {
      const urlBarShowing = window.innerHeight > vv.height + 5;
      setBottomNavInsetPx(urlBarShowing ? 0 : 4);
    };
    update();
    vv.addEventListener('resize', update);
    return () => vv.removeEventListener('resize', update);
  }, []);
  const BOTTOM_NAV_PAGE_SIZE = 5;
  const bottomNavTotalPages = Math.ceil(DASHBOARD_TABS.length / BOTTOM_NAV_PAGE_SIZE);
  const activeTabPage = Math.floor(
    Math.max(0, DASHBOARD_TABS.findIndex((t) => t.key === activeTab)) / BOTTOM_NAV_PAGE_SIZE,
  );
  const [bottomNavPage, setBottomNavPage] = useState(activeTabPage);
  useEffect(() => {
    setBottomNavPage(activeTabPage);
  }, [activeTabPage]);

  // Dashboard view — controls overview tab widget density only
  const dashboardView = userSettings.dashboardView;
  const hiddenWidgets = new Set(userSettings.hiddenWidgets || []);
  const isWidgetVisible = (key: string) => !hiddenWidgets.has(key as any);

  const handleTabChange = useCallback((tab: DashboardTab) => {
    setActiveTab(tab);
    localStorage.setItem(TAB_STORAGE_KEY, tab);
    if (tab !== 'charts') setChartsDefaultTab(undefined);
  }, []);

  // Pro opens the chart dialog over the current page so closing it leaves you
  // where you were; free has no chart dialogs, so it goes to the Charts tab.
  const [overlayChart, setOverlayChart] = useState<ChartTab | undefined>();
  const openChart = useCallback((tab: ChartTab) => {
    if (paymentTier === 'pro') {
      setOverlayChart(tab);
    } else {
      setChartsDefaultTab(tab);
      handleTabChange('charts');
    }
  }, [paymentTier, handleTabChange]);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('tallyup_sidebar_collapsed', String(next));
      return next;
    });
  }, []);

  // Summary strip computed values
  const budgetRemaining = totalBudgeted - totalSpent;
  const goalsProgress = useMemo(() => {
    if (goalProgress.length === 0) return 0;
    const avg = goalProgress.reduce((sum, g) => sum + Math.min(g.percentage, 100), 0) / goalProgress.length;
    return Math.round(avg);
  }, [goalProgress]);

  const totalInvestmentGain = useMemo(() => {
    return calculatePortfolioGain(investments, cryptoHoldings, privateEquityHoldings, retirement401k);
  }, [investments, cryptoHoldings, privateEquityHoldings, retirement401k]);

  const creditUtilization = useMemo(() => {
    return totalCreditLimit > 0 ? Math.round((totalCreditUsed / totalCreditLimit) * 100) : 0;
  }, [totalCreditUsed, totalCreditLimit]);

  // Recent activity: compare latest snapshot to previous
  const recentActivity = useMemo(() => {
    if (netWorthHistory.length < 2) return null;
    const sorted = [...netWorthHistory].sort((a, b) => a.date.localeCompare(b.date));
    const latest = sorted[sorted.length - 1];
    const previous = sorted[sorted.length - 2];
    const nwChange = latest.netWorth - previous.netWorth;
    const assetChange = latest.totalAssets - previous.totalAssets;
    const liabilityChange = latest.totalLiabilities - previous.totalLiabilities;
    const bankingChange = latest.banking - previous.banking;
    const investmentChange = latest.investments - previous.investments;
    return { latest, previous, nwChange, assetChange, liabilityChange, bankingChange, investmentChange };
  }, [netWorthHistory]);

  // Scroll to top on mount (preserves position from landing page otherwise)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Show success message when returning from Stripe checkout
  const [showStripeSuccess, setShowStripeSuccess] = useState(false);

  useEffect(() => {
    if (stripeReturnStatus === 'success') {
      setShowStripeSuccess(true);
      clearStripeReturnStatus();
      // Auto-dismiss after 5 seconds
      const timer = setTimeout(() => setShowStripeSuccess(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [stripeReturnStatus, clearStripeReturnStatus]);

  // The visitor chose Pro on the pricing page, but going straight to Stripe
  // failed (App marks that 'show'), for example because the early adopter
  // price just ended. Open the upgrade screen instead so they can see why and
  // continue. The flag is cleared either way so it only ever fires once.
  useEffect(() => {
    if (!userProfile) return;
    let wantsPro = false;
    try {
      wantsPro = localStorage.getItem(STORAGE_KEYS.UPGRADE_INTENT) === 'show';
      if (wantsPro) localStorage.removeItem(STORAGE_KEYS.UPGRADE_INTENT);
    } catch {
      return;
    }
    if (wantsPro && paymentTier !== 'pro') {
      setUpgradeModalType('general');
      setUpgradeChosePro(true);
      setShowUpgradeModal(true);
    }
  }, [userProfile, paymentTier]);

  const openUpgradeModal = (type:'investments' | 'bankAccounts' | 'retirement401kHoldings' | 'privateEquityHoldings' | 'cryptoHoldings') => {
    setUpgradeModalType(type);
    setShowUpgradeModal(true);
  };

  const openUpgradeFromToolbar = () => {
    setUpgradeModalType('general');
    setShowUpgradeModal(true);
  };

  const handleTutorialComplete = useCallback(() => {
    setRunTutorial(false);
    localStorage.setItem(STORAGE_KEYS.TUTORIAL_COMPLETED, 'true');
    if (user?.id) {
      supabase.from('profiles').update({ tutorial_completed: true }).eq('id', user.id)
        .then(() => {});
    }
  }, [user?.id]);

  const handleReplayTutorial = useCallback(() => {
    setRunTutorial(true);
  }, []);

  const handleSubmitEntity = useCallback(async (data: Record<string, string>) => {
    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    if (!sheetId) {
      setError('No sheet connected');
      return;
    }

    setIsSubmitting(true);

    try {
      let success = true;

      switch (activeModal) {
        case 'investment':
          success = await handleAddInvestment(
            data.symbol,
            parseFloat(data.shares) || 0,
            parseFloat(data.costBasis) || 0,
            sheetId,
            paymentTier,
            data.account || 'Individual'
          );
          if (!success) openUpgradeModal('investments');
          break;

        case 'bankAccount':
          success = await handleAddBankAccount(
            data.name,
            parseFloat(data.balance || '0'),
            sheetId,
            paymentTier
          );
          if (!success) openUpgradeModal('bankAccounts');
          break;

        case 'creditCard':
          await handleAddCreditCard(
            data.name,
            parseFloat(data.balance || '0'),
            parseFloat(data.limit || '5000'),
            sheetId
          );
          break;

        case 'asset':
          await handleAddAsset(
            data.name,
            parseFloat(data.value || '0'),
            sheetId
          );
          break;

        case 'hsa':
          await handleAddHSA(
            data.name,
            parseFloat(data.balance || '0'),
            sheetId
          );
          break;

        case 'creditScore':
          await handleAddCreditScore(
            data.bureau,
            parseFloat(data.score) || 0,
            sheetId
          );
          break;

        case 'liability':
          await handleAddLiability(
            data.name,
            parseFloat(data.value || '0'),
            parseFloat(data.interestRate || '0'),
            sheetId
          );
          break;

        case 'retirement401k':
          success = await handleAdd401kHolding(
            data.fundName,
            data.ticker || '',
            parseFloat(data.shares || '0'),
            parseFloat(data.balance || '0'),
            data.employer || '',
            data.accountType || '401k Traditional',
            parseFloat(data.costBasis || '0'),
            sheetId,
            paymentTier
          );
          if (!success) openUpgradeModal('retirement401kHoldings');
          break;

        case 'privateEquity':
          success = await handleAddPrivateEquity(
            data.name,
            data.symbol || '',
            parseFloat(data.shares || '0'),
            parseFloat(data.pricePerShare || '0'),
            parseFloat(data.costBasis || '0'),
            sheetId,
            paymentTier,
            data.account || 'Individual'
          );
          if (!success) openUpgradeModal('privateEquityHoldings');
          break;

        case 'crypto':
          success = await handleAddCrypto(
            data.symbol,
            data.coinGeckoId || '',
            parseFloat(data.amount || '0'),
            parseFloat(data.costBasis || '0'),
            sheetId,
            paymentTier,
            data.account || 'Main'
          );
          if (!success) openUpgradeModal('cryptoHoldings');
          break;

        case 'budgetCategory':
          await handleAddBudgetCategory(
            data.category,
            parseFloat(data.budgeted || '0'),
            sheetId,
            data.group || ''
          );
          break;

        case 'goal':
          await handleAddGoal(
            data.name,
            parseFloat(data.targetAmount || '0'),
            parseFloat(data.currentAmount || '0'),
            data.targetDate || '',
            data.unit || 'USD',
            sheetId
          );
          break;
      }

      if (success) {
        setActiveModal(null);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to add item';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    activeModal,
    sheetId,
    paymentTier,
    isSubmitting,
    handleAddInvestment,
    handleAddBankAccount,
    handleAddCreditCard,
    handleAddAsset,
    handleAddHSA,
    handleAddCreditScore,
    handleAddLiability,
    handleAdd401kHolding,
    handleAddCrypto,
    handleAddPrivateEquity,
    handleAddBudgetCategory,
    handleAddGoal,
    setError,
  ]);

  const handleCSVImport = useCallback(async (holdings: Array<{ fundName: string; ticker: string; shares: number; balance: number; employer: string; accountType: string; costBasis: number }>) => {
    if (!sheetId) {
      setError('No sheet connected');
      return;
    }
    try {
      await handleBatchAdd401kHoldings(holdings, sheetId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to import CSV';
      setError(errorMessage);
    }
  }, [sheetId, handleBatchAdd401kHoldings, setError]);

  const handleInvestmentCSVImport = useCallback(async (investments: Array<{ symbol: string; shares: number; costBasis: number }>) => {
    if (!sheetId) {
      setError('No sheet connected');
      return;
    }
    try {
      await handleBatchAddInvestments(investments, sheetId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to import investments';
      setError(errorMessage);
    }
  }, [sheetId, handleBatchAddInvestments, setError]);

  const handleExportWithSections = async (sections: PDFExportSections) => {
    try {
      const { default: jsPDF } = await import('jspdf');

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;

      // Helper function to check if we need a new page
      const checkNewPage = (requiredSpace: number) => {
        if (yPosition + requiredSpace > pageHeight - 20) {
          doc.addPage();
          // Apply purple-tinted background to new page
          doc.setFillColor(...[245, 243, 255] as [number, number, number]);
          doc.rect(0, 0, pageWidth, pageHeight, 'F');
          yPosition = 20;
          return true;
        }
        return false;
      };

      // Colors matching TallyUp theme
      const colors = {
        purple: [147, 51, 234] as [number, number, number],
        deepPurple: [88, 28, 135] as [number, number, number],
        violet: [124, 58, 237] as [number, number, number],
        indigo: [99, 102, 241] as [number, number, number],
        pink: [236, 72, 153] as [number, number, number],
        green: [34, 197, 94] as [number, number, number],
        red: [239, 68, 68] as [number, number, number],
        gray: [107, 114, 128] as [number, number, number],
        lightGray: [243, 244, 246] as [number, number, number],
        pageBg: [245, 243, 255] as [number, number, number],
        sectionBg: [237, 233, 254] as [number, number, number],
      };

      // Page background — subtle purple tint matching the app gradient
      doc.setFillColor(...colors.pageBg);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');

      // Header with smooth purple gradient (interpolated strips)
      const headerHeight = 40;
      const numStrips = 500;
      const stripW = pageWidth / numStrips;
      const gradStart: [number, number, number] = [58, 12, 100];   // Dark purple (left edge)
      const gradEnd: [number, number, number] = [139, 92, 246];    // Lighter violet-purple (right edge)
      for (let i = 0; i < numStrips; i++) {
        const t = i / (numStrips - 1);
        const r = Math.round(gradStart[0] * (1 - t) + gradEnd[0] * t);
        const g = Math.round(gradStart[1] * (1 - t) + gradEnd[1] * t);
        const b = Math.round(gradStart[2] * (1 - t) + gradEnd[2] * t);
        doc.setFillColor(r, g, b);
        doc.rect(i * stripW, 0, stripW + 0.5, headerHeight, 'F');
      }

      // Title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont(undefined as unknown as string, 'bold');
      doc.text('TallyUp Finance', 20, 20);
      doc.setFontSize(14);
      doc.setFont(undefined as unknown as string, 'normal');
      doc.text('Financial Snapshot', 20, 30);

      // Date
      doc.setFontSize(10);
      doc.text(new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }), pageWidth - 60, 25);

      yPosition = 50;

      // Net Worth Section
      if (sections.netWorth) {
        doc.setFillColor(...colors.sectionBg);
        doc.roundedRect(20, yPosition, pageWidth - 40, 25, 3, 3, 'F');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(12);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Net Worth', 25, yPosition + 10);
        doc.setFontSize(18);
        doc.text(formatCurrency(netWorth), pageWidth - 25, yPosition + 15, { align: 'right' });
        yPosition += 35;
      }

      // Banking Accounts
      if (sections.banking) {
        checkNewPage(60);
        doc.setFontSize(14);
        doc.setTextColor(...colors.purple);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Banking Accounts', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(...colors.gray);
        doc.setFont(undefined as unknown as string, 'normal');
        bankingData.forEach((account) => {
          checkNewPage(10);
          doc.text(account.name, 25, yPosition);
          doc.setFont(undefined as unknown as string, 'bold');
          doc.setTextColor(...colors.green);
          doc.text(formatCurrency(account.balance), pageWidth - 25, yPosition, { align: 'right' });
          doc.setFont(undefined as unknown as string, 'normal');
          doc.setTextColor(...colors.gray);
          yPosition += 6;
        });

        doc.setFont(undefined as unknown as string, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Total', 25, yPosition);
        doc.setTextColor(...colors.green);
        doc.text(formatCurrency(totalBanking), pageWidth - 25, yPosition, { align: 'right' });
        yPosition += 12;
      }

      // HSA Accounts
      if (sections.hsa && hsaAccounts.length > 0) {
        checkNewPage(60);
        doc.setFontSize(14);
        doc.setTextColor(...colors.purple);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('HSA Accounts', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(...colors.gray);
        doc.setFont(undefined as unknown as string, 'normal');
        hsaAccounts.forEach((account) => {
          checkNewPage(10);
          doc.text(account.name, 25, yPosition);
          doc.setFont(undefined as unknown as string, 'bold');
          doc.setTextColor(...colors.green);
          doc.text(formatCurrency(account.balance), pageWidth - 25, yPosition, { align: 'right' });
          doc.setFont(undefined as unknown as string, 'normal');
          doc.setTextColor(...colors.gray);
          yPosition += 6;
        });

        doc.setFont(undefined as unknown as string, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Total HSA', 25, yPosition);
        doc.setTextColor(...colors.green);
        doc.text(formatCurrency(totalHSA), pageWidth - 25, yPosition, { align: 'right' });
        yPosition += 12;
      }

      // Credit Cards
      if (sections.creditCards) {
        checkNewPage(60);
        doc.setFontSize(14);
        doc.setTextColor(...colors.purple);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Credit Cards', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(...colors.gray);
        doc.setFont(undefined as unknown as string, 'normal');
        creditCards.forEach((card) => {
          checkNewPage(10);
          const utilization = (card.balance / card.limit) * 100;
          doc.text(`${card.name} (${utilization.toFixed(0)}% utilized)`, 25, yPosition);
          doc.setFont(undefined as unknown as string, 'bold');
          doc.text(formatCurrency(card.balance), pageWidth - 25, yPosition, { align: 'right' });
          doc.setFont(undefined as unknown as string, 'normal');
          yPosition += 6;
        });
        yPosition += 6;
      }

      // Investments
      if (sections.investments && investments.length > 0) {
        checkNewPage(60);
        doc.setFontSize(14);
        doc.setTextColor(...colors.purple);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Investment Portfolio', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(...colors.gray);
        doc.setFont(undefined as unknown as string, 'normal');
        investments.forEach((inv) => {
          checkNewPage(10);
          const { percentage } = calculateReturn(inv.shares, inv.costBasis, inv.currentPrice);
          const isPositive = percentage >= 0;

          doc.text(`${inv.symbol} (${formatNumber(inv.shares, 0)} shares)`, 25, yPosition);
          doc.setFont(undefined as unknown as string, 'bold');
          if (isPositive) {
            doc.setTextColor(...colors.green);
          } else {
            doc.setTextColor(...colors.red);
          }
          doc.text(`${isPositive ? '+' : ''}${percentage.toFixed(1)}%`, pageWidth - 60, yPosition, { align: 'right' });
          doc.text(formatCurrency(inv.totalValue), pageWidth - 25, yPosition, { align: 'right' });
          doc.setFont(undefined as unknown as string, 'normal');
          doc.setTextColor(...colors.gray);
          yPosition += 6;
        });

        doc.setFont(undefined as unknown as string, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Total Portfolio', 25, yPosition);
        doc.setTextColor(...colors.purple);
        doc.text(formatCurrency(totalInvestmentValue), pageWidth - 25, yPosition, { align: 'right' });
        yPosition += 12;
      }

      // 401k Holdings
      if (sections.retirement401k && retirement401k.length > 0) {
        checkNewPage(60);
        doc.setFontSize(14);
        doc.setTextColor(...colors.purple);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('401k Retirement', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(...colors.gray);
        doc.setFont(undefined as unknown as string, 'normal');
        retirement401k.forEach((holding) => {
          checkNewPage(10);
          const label = holding.ticker ? `${holding.fundName} (${holding.ticker})` : holding.fundName;
          doc.text(label, 25, yPosition);
          doc.setFont(undefined as unknown as string, 'bold');
          doc.setTextColor(...colors.green);
          doc.text(formatCurrency(holding.balance), pageWidth - 25, yPosition, { align: 'right' });
          doc.setFont(undefined as unknown as string, 'normal');
          doc.setTextColor(...colors.gray);
          yPosition += 6;
        });

        doc.setFont(undefined as unknown as string, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Total 401k', 25, yPosition);
        doc.setTextColor(...colors.purple);
        doc.text(formatCurrency(total401k), pageWidth - 25, yPosition, { align: 'right' });
        yPosition += 12;
      }

      // Crypto Holdings
      if (sections.crypto && cryptoHoldings.length > 0) {
        checkNewPage(60);
        doc.setFontSize(14);
        doc.setTextColor(245, 158, 11); // amber-500
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Crypto Holdings', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(...colors.gray);
        doc.setFont(undefined as unknown as string, 'normal');
        cryptoHoldings.forEach((holding) => {
          checkNewPage(10);
          const gain = holding.totalValue - (holding.amount * holding.costBasis);
          const gainPct = (holding.amount * holding.costBasis) > 0
            ? (gain / (holding.amount * holding.costBasis)) * 100
            : 0;
          const isPositive = gainPct >= 0;

          doc.text(`${holding.symbol} (${formatNumber(holding.amount, 4)})`, 25, yPosition);
          doc.setFont(undefined as unknown as string, 'bold');
          if (isPositive) {
            doc.setTextColor(...colors.green);
          } else {
            doc.setTextColor(...colors.red);
          }
          doc.text(`${isPositive ? '+' : ''}${gainPct.toFixed(1)}%`, pageWidth - 60, yPosition, { align: 'right' });
          doc.text(formatCurrency(holding.totalValue), pageWidth - 25, yPosition, { align: 'right' });
          doc.setFont(undefined as unknown as string, 'normal');
          doc.setTextColor(...colors.gray);
          yPosition += 6;
        });

        doc.setFont(undefined as unknown as string, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Total Crypto', 25, yPosition);
        doc.setTextColor(245, 158, 11);
        doc.text(formatCurrency(totalCrypto), pageWidth - 25, yPosition, { align: 'right' });
        yPosition += 12;
      }

      // Private Equity Holdings
      if (sections.privateEquity && privateEquityHoldings.length > 0) {
        checkNewPage(60);
        doc.setFontSize(14);
        doc.setTextColor(...colors.purple);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Private Equity', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(...colors.gray);
        doc.setFont(undefined as unknown as string, 'normal');
        privateEquityHoldings.forEach((holding) => {
          checkNewPage(10);
          const gain = holding.totalValue - holding.costBasis;
          const gainPct = holding.costBasis > 0 ? (gain / holding.costBasis) * 100 : 0;
          const isPositive = gainPct >= 0;
          const label = holding.symbol ? `${holding.name} (${holding.symbol})` : holding.name;

          doc.text(`${label} (${formatNumber(holding.shares, 0)} shares)`, 25, yPosition);
          doc.setFont(undefined as unknown as string, 'bold');
          if (isPositive) {
            doc.setTextColor(...colors.green);
          } else {
            doc.setTextColor(...colors.red);
          }
          doc.text(`${isPositive ? '+' : ''}${gainPct.toFixed(1)}%`, pageWidth - 60, yPosition, { align: 'right' });
          doc.text(formatCurrency(holding.totalValue), pageWidth - 25, yPosition, { align: 'right' });
          doc.setFont(undefined as unknown as string, 'normal');
          doc.setTextColor(...colors.gray);
          yPosition += 6;
        });

        doc.setFont(undefined as unknown as string, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Total Private Equity', 25, yPosition);
        doc.setTextColor(...colors.purple);
        doc.text(formatCurrency(totalPrivateEquity), pageWidth - 25, yPosition, { align: 'right' });
        yPosition += 12;
      }

      // Assets
      if (sections.assets) {
        checkNewPage(60);
        doc.setFontSize(14);
        doc.setTextColor(...colors.purple);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Assets', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(...colors.gray);
        doc.setFont(undefined as unknown as string, 'normal');
        assets.forEach((asset) => {
          checkNewPage(10);
          doc.text(asset.name, 25, yPosition);
          doc.setFont(undefined as unknown as string, 'bold');
          doc.setTextColor(...colors.green);
          doc.text(formatCurrency(asset.value), pageWidth - 25, yPosition, { align: 'right' });
          doc.setFont(undefined as unknown as string, 'normal');
          doc.setTextColor(...colors.gray);
          yPosition += 6;
        });

        doc.setFont(undefined as unknown as string, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Total Assets', 25, yPosition);
        doc.setTextColor(...colors.green);
        doc.text(formatCurrency(totalAssets), pageWidth - 25, yPosition, { align: 'right' });
        yPosition += 12;
      }

      // Liabilities
      if (sections.liabilities) {
        checkNewPage(60);
        doc.setFontSize(14);
        doc.setTextColor(...colors.purple);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Liabilities', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(...colors.gray);
        doc.setFont(undefined as unknown as string, 'normal');
        liabilities.forEach((liability) => {
          checkNewPage(10);
          const aprLabel = liability.interestRate > 0 ? ` (${liability.interestRate}% APR)` : '';
          doc.text(`${liability.name}${aprLabel}`, 25, yPosition);
          doc.setFont(undefined as unknown as string, 'bold');
          doc.setTextColor(...colors.red);
          doc.text(formatCurrency(liability.value), pageWidth - 25, yPosition, { align: 'right' });
          doc.setFont(undefined as unknown as string, 'normal');
          doc.setTextColor(...colors.gray);
          yPosition += 6;
        });

        doc.setFont(undefined as unknown as string, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Total Liabilities', 25, yPosition);
        doc.setTextColor(...colors.red);
        doc.text(formatCurrency(totalLiabilities), pageWidth - 25, yPosition, { align: 'right' });
        yPosition += 12;
      }

      // Budget Summary
      if (sections.budget && budgetCategories.length > 0) {
        checkNewPage(60);
        doc.setFontSize(14);
        doc.setTextColor(...colors.purple);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Monthly Budget', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(...colors.gray);
        doc.setFont(undefined as unknown as string, 'normal');
        budgetCategories.forEach((cat) => {
          checkNewPage(10);
          const pct = cat.budgeted > 0 ? ((cat.spent / cat.budgeted) * 100).toFixed(0) : '0';
          doc.text(`${cat.category} (${pct}% used)`, 25, yPosition);
          doc.setFont(undefined as unknown as string, 'bold');
          doc.setTextColor(...(cat.spent > cat.budgeted ? colors.red : colors.green));
          doc.text(`${formatCurrency(cat.spent)} / ${formatCurrency(cat.budgeted)}`, pageWidth - 25, yPosition, { align: 'right' });
          doc.setFont(undefined as unknown as string, 'normal');
          doc.setTextColor(...colors.gray);
          yPosition += 6;
        });

        doc.setFont(undefined as unknown as string, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Remaining', 25, yPosition);
        const budgetRemaining = totalBudgeted - totalSpent;
        doc.setTextColor(...(budgetRemaining >= 0 ? colors.green : colors.red));
        doc.text(formatCurrency(budgetRemaining), pageWidth - 25, yPosition, { align: 'right' });
        yPosition += 12;
      }

      // Goals Progress
      if (sections.goals && goals.length > 0) {
        checkNewPage(60);
        doc.setFontSize(14);
        doc.setTextColor(...colors.purple);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Financial Goals', 20, yPosition);
        yPosition += 8;

        doc.setFontSize(10);
        doc.setTextColor(...colors.gray);
        doc.setFont(undefined as unknown as string, 'normal');
        goals.forEach((goal) => {
          checkNewPage(10);
          const pct = goal.targetAmount > 0 ? ((goal.currentAmount / goal.targetAmount) * 100).toFixed(0) : '0';
          const label = goal.targetDate ? `${goal.name} (by ${goal.targetDate})` : goal.name;
          doc.text(`${label} - ${pct}%`, 25, yPosition);
          doc.setFont(undefined as unknown as string, 'bold');
          doc.setTextColor(...colors.green);
          doc.text(`${formatCurrency(goal.currentAmount)} / ${formatCurrency(goal.targetAmount)}`, pageWidth - 25, yPosition, { align: 'right' });
          doc.setFont(undefined as unknown as string, 'normal');
          doc.setTextColor(...colors.gray);
          yPosition += 6;
        });

        doc.setFont(undefined as unknown as string, 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text('Total Progress', 25, yPosition);
        const goalPct = totalGoalsTarget > 0 ? ((totalGoalsSaved / totalGoalsTarget) * 100).toFixed(0) : '0';
        doc.setTextColor(...colors.purple);
        doc.text(`${goalPct}% (${formatCurrency(totalGoalsSaved)} / ${formatCurrency(totalGoalsTarget)})`, pageWidth - 25, yPosition, { align: 'right' });
        yPosition += 12;
      }

      // ========== PRO CHARTS SECTION ==========
      if (sections.charts) {
      // New page for charts
      doc.addPage();
      // Apply purple-tinted background
      doc.setFillColor(...colors.pageBg);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      yPosition = 20;

      // Charts header with smooth purple gradient
      const proHeaderH = 30;
      const proStrips = 500;
      const proStripW = pageWidth / proStrips;
      for (let i = 0; i < proStrips; i++) {
        const t = i / (proStrips - 1);
        const r = Math.round(gradStart[0] * (1 - t) + gradEnd[0] * t);
        const g = Math.round(gradStart[1] * (1 - t) + gradEnd[1] * t);
        const b = Math.round(gradStart[2] * (1 - t) + gradEnd[2] * t);
        doc.setFillColor(r, g, b);
        doc.rect(i * proStripW, 0, proStripW + 0.5, proHeaderH, 'F');
      }
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont(undefined as unknown as string, 'bold');
      doc.text('Financial Analysis', 20, 18);
      doc.setFontSize(10);
      doc.setFont(undefined as unknown as string, 'normal');
      doc.text('Pro Report', pageWidth - 30, 18);

      yPosition = 40;

      // === NET WORTH BREAKDOWN ===
      doc.setFontSize(14);
      doc.setTextColor(...colors.purple);
      doc.setFont(undefined as unknown as string, 'bold');
      doc.text('Net Worth Breakdown', 20, yPosition);
      yPosition += 10;

      // Assets vs Liabilities bar chart
      const barWidth = pageWidth - 60;
      const barHeight = 20;
      const maxValue = Math.max(totalAssets, totalLiabilities);

      // Assets bar
      doc.setFillColor(...colors.sectionBg);
      doc.roundedRect(30, yPosition, barWidth, barHeight, 2, 2, 'F');
      const assetsWidth = maxValue > 0 ? (totalAssets / maxValue) * barWidth : 0;
      doc.setFillColor(...colors.green);
      doc.roundedRect(30, yPosition, assetsWidth, barHeight, 2, 2, 'F');
      doc.setFontSize(10);
      doc.setFont(undefined as unknown as string, 'bold');
      const assetsLabel = `Assets: ${formatCurrency(totalAssets)}`;
      const assetsLabelWidth = doc.getTextWidth(assetsLabel) + 10;
      if (assetsWidth > assetsLabelWidth) {
        doc.setTextColor(255, 255, 255);
        doc.text(assetsLabel, 35, yPosition + 13);
      } else {
        doc.setTextColor(...colors.green);
        doc.text(assetsLabel, 35 + assetsWidth + 5, yPosition + 13);
      }
      yPosition += 25;

      // Liabilities bar
      doc.setFillColor(...colors.sectionBg);
      doc.roundedRect(30, yPosition, barWidth, barHeight, 2, 2, 'F');
      const liabilitiesWidth = maxValue > 0 ? (totalLiabilities / maxValue) * barWidth : 0;
      doc.setFillColor(...colors.red);
      doc.roundedRect(30, yPosition, liabilitiesWidth, barHeight, 2, 2, 'F');
      const liabLabel = `Liabilities: ${formatCurrency(totalLiabilities)}`;
      const liabLabelWidth = doc.getTextWidth(liabLabel) + 10;
      if (liabilitiesWidth > liabLabelWidth) {
        doc.setTextColor(255, 255, 255);
        doc.text(liabLabel, 35, yPosition + 13);
      } else {
        doc.setTextColor(...colors.red);
        doc.text(liabLabel, 35 + liabilitiesWidth + 5, yPosition + 13);
      }
      yPosition += 30;

      // Breakdown details
      doc.setFontSize(10);
      doc.setTextColor(...colors.gray);
      doc.setFont(undefined as unknown as string, 'normal');

      const amber: [number, number, number] = [245, 158, 11];
      const breakdownItems = [
        { label: 'Banking Accounts', value: totalBanking, color: colors.green },
        { label: 'Investments', value: totalInvestmentValue, color: colors.purple },
        { label: '401k Retirement', value: total401k, color: colors.purple },
        { label: 'Private Equity', value: totalPrivateEquity, color: colors.purple },
        { label: 'Crypto', value: totalCrypto, color: amber },
        { label: 'Credit Cards', value: totalCreditUsed, color: colors.red },
        { label: 'Liabilities', value: totalLiabilities, color: colors.pink },
      ];

      breakdownItems.forEach((item) => {
        doc.setTextColor(...colors.gray);
        doc.text(item.label, 30, yPosition);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.setTextColor(...item.color);
        doc.text(formatCurrency(item.value), pageWidth - 30, yPosition, { align: 'right' });
        doc.setFont(undefined as unknown as string, 'normal');
        yPosition += 7;
      });

      yPosition += 10;

      // === INVESTMENT RETURNS ===
      if (investments.length > 0) {
        checkNewPage(80);
        doc.setFontSize(14);
        doc.setTextColor(...colors.purple);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Investment Returns Analysis', 20, yPosition);
        yPosition += 12;

        // Table header
        doc.setFillColor(...colors.purple);
        doc.rect(20, yPosition, pageWidth - 40, 8, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Symbol', 25, yPosition + 5.5);
        doc.text('Shares', 55, yPosition + 5.5);
        doc.text('Cost Basis', 85, yPosition + 5.5);
        doc.text('Current', 115, yPosition + 5.5);
        doc.text('Value', 145, yPosition + 5.5);
        doc.text('Return', pageWidth - 25, yPosition + 5.5, { align: 'right' });
        yPosition += 12;

        // Table rows
        investments.forEach((inv, idx) => {
          checkNewPage(10);
          const { percentage } = calculateReturn(inv.shares, inv.costBasis, inv.currentPrice);
          const isPositive = percentage >= 0;
          const rowBg = idx % 2 === 0 ? colors.sectionBg : colors.pageBg;

          doc.setFillColor(...rowBg);
          doc.rect(20, yPosition - 4, pageWidth - 40, 8, 'F');

          doc.setTextColor(0, 0, 0);
          doc.setFontSize(8);
          doc.setFont(undefined as unknown as string, 'bold');
          doc.text(inv.symbol, 25, yPosition + 1);
          doc.setFont(undefined as unknown as string, 'normal');
          doc.text(formatNumber(inv.shares, 2), 55, yPosition + 1);
          doc.text(formatCurrency(inv.costBasis), 85, yPosition + 1);
          doc.text(formatCurrency(inv.currentPrice), 115, yPosition + 1);
          doc.text(formatCurrency(inv.totalValue), 145, yPosition + 1);

          doc.setFont(undefined as unknown as string, 'bold');
          if (isPositive) {
            doc.setTextColor(...colors.green);
          } else {
            doc.setTextColor(...colors.red);
          }
          doc.text(`${isPositive ? '+' : ''}${percentage.toFixed(1)}%`, pageWidth - 25, yPosition + 1, { align: 'right' });

          yPosition += 8;
        });

        // Total row
        const totalGain = investments.reduce((sum, inv) => {
          const { gain } = calculateReturn(inv.shares, inv.costBasis, inv.currentPrice);
          return sum + gain;
        }, 0);
        const totalCost = investments.reduce((sum, inv) => sum + (inv.shares * inv.costBasis), 0);
        const totalReturn = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;

        doc.setFillColor(...colors.purple);
        doc.rect(20, yPosition - 2, pageWidth - 40, 10, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(9);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('TOTAL', 25, yPosition + 4);
        doc.text(formatCurrency(totalInvestmentValue), 145, yPosition + 4);
        doc.text(`${totalReturn >= 0 ? '+' : ''}${totalReturn.toFixed(1)}%`, pageWidth - 25, yPosition + 4, { align: 'right' });
        yPosition += 15;
      }

      // === ASSET ALLOCATION PIE CHART ===
      checkNewPage(120);
      doc.setFontSize(14);
      doc.setTextColor(...colors.purple);
      doc.setFont(undefined as unknown as string, 'bold');
      doc.text('Asset Allocation', 20, yPosition);
      yPosition += 8;

      // Build allocation data with distinct colors
      const pieColors: Array<[number, number, number]> = [
        [59, 130, 246],   // Blue
        [139, 92, 246],   // Purple
        [236, 72, 153],   // Pink
        [245, 158, 11],   // Amber
        [16, 185, 129],   // Green
        [6, 182, 212],    // Cyan
        [99, 102, 241],   // Indigo
        [249, 115, 22],   // Orange
        [20, 184, 166],   // Teal
        [168, 85, 247],   // Violet
      ];

      const allocationData: Array<{ name: string; value: number; color: [number, number, number] }> = [];

      bankingData.forEach((account) => {
        if (account.balance > 0) {
          allocationData.push({ name: account.name, value: account.balance, color: pieColors[allocationData.length % pieColors.length] });
        }
      });

      investments.forEach((inv) => {
        if (inv.totalValue > 0) {
          allocationData.push({ name: inv.symbol, value: inv.totalValue, color: pieColors[allocationData.length % pieColors.length] });
        }
      });

      retirement401k.forEach((holding) => {
        if (holding.balance > 0) {
          allocationData.push({ name: holding.ticker || holding.fundName, value: holding.balance, color: pieColors[allocationData.length % pieColors.length] });
        }
      });

      privateEquityHoldings.forEach((holding) => {
        if (holding.totalValue > 0) {
          allocationData.push({ name: holding.symbol || holding.name, value: holding.totalValue, color: pieColors[allocationData.length % pieColors.length] });
        }
      });

      cryptoHoldings.forEach((crypto) => {
        if (crypto.totalValue > 0) {
          allocationData.push({ name: crypto.symbol, value: crypto.totalValue, color: pieColors[allocationData.length % pieColors.length] });
        }
      });

      hsaAccounts.forEach((hsa) => {
        if (hsa.balance > 0) {
          allocationData.push({ name: hsa.name, value: hsa.balance, color: pieColors[allocationData.length % pieColors.length] });
        }
      });

      assets.forEach((asset) => {
        if (asset.value > 0 && asset.name !== 'Investment Portfolio') {
          allocationData.push({ name: asset.name, value: asset.value, color: pieColors[allocationData.length % pieColors.length] });
        }
      });

      // Sort by value descending
      allocationData.sort((a, b) => b.value - a.value);

      // Reassign colors after sorting
      allocationData.forEach((item, idx) => {
        item.color = pieColors[idx % pieColors.length];
      });

      // Calculate total for percentages
      const pieTotal = allocationData.reduce((sum, item) => sum + item.value, 0);

      if (allocationData.length > 0 && pieTotal > 0) {
        // Pie chart settings
        const centerX = 60;
        const centerY = yPosition + 45;
        const radius = 35;

        // Draw pie segments
        let startAngle = -Math.PI / 2; // Start from top

        allocationData.slice(0, 10).forEach((item) => {
          const percentage = item.value / pieTotal;
          const sweepAngle = percentage * 2 * Math.PI;
          const endAngle = startAngle + sweepAngle;

          // Draw pie segment using lines
          doc.setFillColor(...item.color);

          // Create path for pie slice
          const segments = Math.max(Math.ceil(sweepAngle / 0.1), 2);
          const angleStep = sweepAngle / segments;

          // Build the slice path
          const points: Array<[number, number]> = [[centerX, centerY]];

          for (let i = 0; i <= segments; i++) {
            const angle = startAngle + (i * angleStep);
            const x = centerX + radius * Math.cos(angle);
            const y = centerY + radius * Math.sin(angle);
            points.push([x, y]);
          }

          // Draw the filled polygon
          if (points.length > 2) {
            doc.setFillColor(...item.color);
            // @ts-ignore - lines method accepts this format
            doc.triangle(
              points[0][0], points[0][1],
              points[1][0], points[1][1],
              points[Math.floor(points.length / 2)][0], points[Math.floor(points.length / 2)][1],
              'F'
            );

            // For larger slices, draw additional triangles to fill
            for (let i = 1; i < points.length - 2; i++) {
              doc.triangle(
                centerX, centerY,
                points[i][0], points[i][1],
                points[i + 1][0], points[i + 1][1],
                'F'
              );
            }
          }

          startAngle = endAngle;
        });

        // Draw white center circle for donut effect
        doc.setFillColor(255, 255, 255);
        doc.circle(centerX, centerY, radius * 0.5, 'F');

        // Total in center
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(7);
        doc.setFont(undefined as unknown as string, 'bold');
        doc.text('Total', centerX, centerY - 3, { align: 'center' });
        doc.setFontSize(6);
        doc.text(formatCurrency(pieTotal), centerX, centerY + 3, { align: 'center' });

        // Legend on the right side
        const legendX = 110;
        let legendY = yPosition + 5;

        allocationData.forEach((item, idx) => {
          const percentage = pieTotal > 0 ? (item.value / pieTotal) * 100 : 0;

          // Color box
          doc.setFillColor(...item.color);
          doc.rect(legendX, legendY - 3, 4, 4, 'F');

          // Name
          doc.setTextColor(...colors.gray);
          doc.setFontSize(7);
          doc.setFont(undefined as unknown as string, 'normal');
          const displayName = item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name;
          doc.text(displayName, legendX + 6, legendY);

          // Value and percentage
          doc.setTextColor(0, 0, 0);
          doc.setFont(undefined as unknown as string, 'bold');
          doc.text(`${formatCurrency(item.value)}`, legendX + 50, legendY);
          doc.setFont(undefined as unknown as string, 'normal');
          doc.setTextColor(...colors.gray);
          doc.text(`(${percentage.toFixed(1)}%)`, legendX + 75, legendY);

          legendY += 8;
        });

        yPosition = Math.max(centerY + radius + 10, legendY + 5);
      } else {
        doc.setTextColor(...colors.gray);
        doc.setFontSize(10);
        doc.text('No assets with positive value to display', 25, yPosition + 10);
        yPosition += 20;
      }
      } // end sections.charts

      // Footer on last page
      doc.setFontSize(8);
      doc.setTextColor(...colors.gray);
      doc.setFont(undefined as unknown as string, 'normal');
      doc.text('Generated by TallyUp Finance Pro - Your Financial Dashboard', pageWidth / 2, pageHeight - 10, { align: 'center' });

      // Save the PDF
      doc.save(`TallyUp-Finance-Report-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      setError('Failed to generate PDF report');
    }
  };

  return (
    <div
      className={`min-h-screen max-w-full overflow-x-hidden flex flex-col ${
        darkMode
          ? 'bg-slate-950'
          : 'bg-[#f8f9ff]'
      } antialiased`}
    >
      {/* Universal Loading Bar */}
      <LoadingBar isLoading={isRefreshing || isSubmitting || isLoading} />
      <ConsentBanner />

      {/* Stripe Success Toast */}
      {showStripeSuccess && (
        <Toast
          message="Welcome to TallyUp Finance Pro! Your upgrade is activated."
          variant="success"
          duration={5000}
          onDismiss={() => setShowStripeSuccess(false)}
        />
      )}

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => {
            setShowUpgradeModal(false);
            setUpgradeChosePro(false);
          }}
          limitType={upgradeModalType}
          chosePro={upgradeChosePro}
        />
      )}

      {/* Tutorial Tour */}
      {/* Held while the upgrade screen is open so the two never overlap. */}
      <TutorialTour run={runTutorial && !showUpgradeModal} onComplete={handleTutorialComplete} />

      {/* Settings Modal */}
      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => { setShowSettingsModal(false); setHasNewUpdates(hasUnseenUpdates()); }}
        onReplayTutorial={handleReplayTutorial}
      />

      {/* CSV Import Modals */}
      <CSVImportModal
        isOpen={showCSVImport}
        onClose={() => setShowCSVImport(false)}
        onImport={handleCSVImport}
        mode="401k"
      />
      <CSVImportModal
        isOpen={showInvestmentCSVImport}
        onClose={() => setShowInvestmentCSVImport(false)}
        onImport={handleInvestmentCSVImport}
        mode="investment"
      />

      {/* Add Entity Modals */}
      {activeModal && (
        <AddEntityModal
          isOpen={true}
          onClose={() => setActiveModal(null)}
          entityType={activeModal}
          onSubmit={handleSubmitEntity}
          error={error}
        />
      )}

      {/* PDF Export Modal */}
      <PDFExportModal
        isOpen={showPDFExportModal}
        onClose={() => setShowPDFExportModal(false)}
        onExport={handleExportWithSections}
      />

      <StreakCelebration />

      {/* Header */}
      <Header
        onRefresh={refreshData}
        onExportPDF={paymentTier === 'pro' ? async () => setShowPDFExportModal(true) : undefined}
        isRefreshing={isRefreshing}
        onUpgradeClick={openUpgradeFromToolbar}
        onSettingsClick={() => setShowSettingsModal(true)}
        hasNewUpdates={hasNewUpdates}
      />

      {/* Sidebar + Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <aside
          className={`hidden md:flex flex-col flex-shrink-0 transition-all duration-300 ${
            sidebarCollapsed ? 'w-16' : 'w-56'
          } ${
            darkMode
              ? 'bg-slate-950 border-r border-slate-800'
              : 'bg-[#f8f9ff] border-r border-slate-200'
          }`}
        >
          <nav data-tour="sidebar-nav" className="flex-1 py-3 space-y-1 px-2">
            {DASHBOARD_TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabChange(tab.key)}
                  title={sidebarCollapsed ? tab.label : undefined}
                  className={`flex items-center gap-3 w-full rounded-xl transition-all duration-200 ${
                    sidebarCollapsed ? 'px-3 py-3 justify-center' : 'px-3 py-2.5'
                  } ${
                    isActive
                      ? darkMode
                        ? 'bg-slate-800 text-violet-400 shadow-sm'
                        : 'bg-white text-[#6b38d4] shadow-sm'
                      : darkMode
                        ? 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                        : 'text-slate-500 hover:text-[#6b38d4] hover:bg-[#eff4ff]'
                  }`}
                >
                  <Icon size={20} className="flex-shrink-0" />
                  {!sidebarCollapsed && (
                    <span className="text-sm font-medium truncate flex items-center gap-1.5">
                      {tab.label}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Collapse Toggle */}
          <div className={`px-2 py-3 border-t ${darkMode ? 'border-slate-800' : 'border-slate-200'}`}>
            <button
              onClick={toggleSidebar}
              className={`flex items-center gap-3 w-full rounded-xl px-3 py-2.5 transition-colors ${
                sidebarCollapsed ? 'justify-center' : ''
              } ${
                darkMode
                  ? 'text-slate-500 hover:bg-slate-800/50 hover:text-slate-300'
                  : 'text-slate-400 hover:bg-[#eff4ff] hover:text-slate-600'
              }`}
              title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {sidebarCollapsed ? <PanelLeft size={20} /> : <PanelLeftClose size={20} />}
              {!sidebarCollapsed && <span className="text-sm">Collapse</span>}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 overflow-y-auto pb-20 md:pb-0 flex flex-col">
          {/* Error Display */}
          {(googleTokenFailed || error) && (
            <div className="px-4 md:px-6 pt-4">
              {googleTokenFailed ? (
                <div className="p-3 rounded-lg bg-amber-500/20 border border-amber-500/50 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-amber-100">
                    Your Google Sheets connection needs to be refreshed. Reconnect to keep your data in sync.
                  </p>
                  <button
                    type="button"
                    onClick={reconnectGoogle}
                    className="shrink-0 px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-gray-900 text-sm font-medium transition-colors"
                  >
                    Reconnect Google
                  </button>
                </div>
              ) : (
                <div className="p-3 rounded-lg bg-red-500/20 border border-red-500/50">
                  <p className="text-sm text-red-200">{error}</p>
                </div>
              )}
            </div>
          )}

          <div className="flex-1 px-4 pt-4 pb-8 md:px-8 md:pt-6 md:pb-10 space-y-4">
            {/* Overview / Dashboard */}
            {activeTab === 'overview' && (
              <>
                {/* Summary Cards */}
                <div data-tour="dashboard-overview" className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
                  {[
                    { label: 'Net Worth', value: formatCurrency(netWorth, privacyMode), labelColor: darkMode ? 'text-violet-300' : 'text-violet-600' },
                    { label: 'Assets', value: formatCurrency(totalAssets, privacyMode), labelColor: darkMode ? 'text-emerald-300' : 'text-emerald-600' },
                    { label: 'Liabilities', value: formatCurrency(totalLiabilities + totalCreditUsed, privacyMode), labelColor: darkMode ? 'text-red-300' : 'text-red-600' },
                    { label: 'Budget Left', value: formatCurrency(budgetRemaining, privacyMode), labelColor: darkMode ? 'text-blue-300' : 'text-blue-600', valueColor: budgetRemaining < 0 ? 'text-red-500' : undefined },
                    { label: 'Goals', value: privacyMode ? '••••••' : `${goalsProgress}%`, labelColor: darkMode ? 'text-amber-300' : 'text-amber-600', colSpan: true },
                  ].map(card => (
                    <div key={card.label} className={`rounded-xl p-4 ${card.colSpan ? 'col-span-2 lg:col-span-1' : ''} ${
                      darkMode
                        ? 'bg-slate-800/50 border border-slate-700/30'
                        : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                    }`}>
                      <p className={`text-xs font-medium mb-1 ${card.labelColor}`}>{card.label}</p>
                      <p className={`text-sm md:text-xl font-bold truncate ${card.valueColor || (darkMode ? 'text-white' : 'text-slate-900')}`}>
                        {card.value}
                      </p>
                    </div>
                  ))}
                </div>

                {dashboardView === 'full' && <MarketSnapshotWidget />}

                {/* Recent Activity */}
                {recentActivity && (
                  <div className={`rounded-2xl p-5 md:p-6 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <div className="flex items-center gap-2 mb-4">
                      <Calendar size={16} className={darkMode ? 'text-slate-400' : 'text-slate-500'} />
                      <h3 className={`text-sm font-bold uppercase tracking-wider ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Since Last Snapshot
                      </h3>
                      <span className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                        {recentActivity.previous.date}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                      {[
                        { label: 'Net Worth', value: recentActivity.nwChange },
                        { label: 'Assets', value: recentActivity.assetChange },
                        { label: 'Liabilities', value: recentActivity.liabilityChange, invert: true },
                        { label: 'Banking', value: recentActivity.bankingChange },
                        { label: 'Investments', value: recentActivity.investmentChange },
                      ].map(({ label, value, invert }) => {
                        const isPositive = invert ? value < 0 : value > 0;
                        const isNegative = invert ? value > 0 : value < 0;
                        const Icon = isPositive ? ArrowUpRight : isNegative ? ArrowDownRight : Minus;
                        const color = isPositive
                          ? (darkMode ? 'text-emerald-400' : 'text-emerald-600')
                          : isNegative
                            ? (darkMode ? 'text-red-400' : 'text-red-600')
                            : (darkMode ? 'text-slate-400' : 'text-slate-500');
                        return (
                          <div key={label}>
                            <p className={`text-xs mb-1 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
                            <div className={`flex items-center gap-1 font-bold text-sm ${color}`}>
                              <Icon size={14} />
                              <span>{privacyMode ? '••••' : formatCurrency(Math.abs(value))}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Inline Visualizations */}
                {dashboardView !== 'overview' && <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Net Worth Sparkline */}
                  <button
                    onClick={() => openChart('history')}
                    className={`text-left rounded-xl p-5 transition-all hover:scale-[1.02] flex flex-col justify-start ${
                      darkMode
                        ? 'bg-slate-800/50 hover:bg-slate-800/70 border border-slate-700/30 shadow-sm'
                        : 'bg-white/70 hover:bg-white backdrop-blur-xl border border-transparent shadow-[0_32px_32px_rgba(107,56,212,0.04)] hover:shadow-[0_32px_32px_rgba(107,56,212,0.08)]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Net Worth Trend</h3>
                    </div>
                    {(() => {
                      const sorted = [...netWorthHistory].sort((a, b) => a.date.localeCompare(b.date));
                      if (sorted.length < 2) {
                        return (
                          <div className={`text-center py-6 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            Not enough history yet
                          </div>
                        );
                      }
                      const points = calculateLineChartPoints(sorted, 'netWorth', 200, 60, 8);
                      const firstPt = points.split(' ')[0];
                      const lastPt = points.split(' ').slice(-1)[0];
                      const polyBottom = `${lastPt?.split(',')[0]},${60 - 8} ${firstPt?.split(',')[0]},${60 - 8}`;
                      return (
                        <>
                          <svg viewBox="0 0 200 60" className="w-full h-auto">
                            <defs>
                              <linearGradient id="sparkGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={HISTORY_LINE_COLORS.netWorth} stopOpacity="0.3" />
                                <stop offset="100%" stopColor={HISTORY_LINE_COLORS.netWorth} stopOpacity="0" />
                              </linearGradient>
                            </defs>
                            <polygon points={`${points} ${polyBottom}`} fill="url(#sparkGrad)" />
                            <polyline points={points} fill="none" stroke={HISTORY_LINE_COLORS.netWorth} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <div className="flex justify-between mt-1">
                            <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{sorted[0].date}</span>
                            <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>{sorted[sorted.length - 1].date}</span>
                          </div>
                        </>
                      );
                    })()}
                  </button>

                  {/* Asset Allocation Mini Donut */}
                  <button
                    onClick={() => openChart('allocation')}
                    className={`text-left rounded-xl p-5 transition-all hover:scale-[1.02] flex flex-col justify-start ${
                      darkMode
                        ? 'bg-slate-800/50 hover:bg-slate-800/70 border border-slate-700/30 shadow-sm'
                        : 'bg-white/70 hover:bg-white backdrop-blur-xl border border-transparent shadow-[0_32px_32px_rgba(107,56,212,0.04)] hover:shadow-[0_32px_32px_rgba(107,56,212,0.08)]'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Asset Allocation</h3>
                    </div>
                    {(() => {
                      const positiveItems = chartData.filter(d => d.value > 0);
                      if (positiveItems.length === 0) {
                        return (
                          <div className={`text-center py-6 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            No asset data yet
                          </div>
                        );
                      }
                      const donutTotal = positiveItems.reduce((s, d) => s + d.value, 0);
                      let cumPct = 0;
                      const segments = positiveItems.map(d => {
                        const pct = (d.value / donutTotal) * 100;
                        const start = cumPct;
                        cumPct += pct;
                        return { ...d, pct, start };
                      });
                      const r = 42;
                      const circ = 2 * Math.PI * r;
                      return (
                        <div className="flex justify-center">
                          <div className="relative w-44 h-44">
                            <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                              {segments.map((seg, i) => (
                                <circle key={i} cx="50" cy="50" r={r} fill="none" stroke={seg.color} strokeWidth="15"
                                  strokeDasharray={`${(seg.pct / 100) * circ} ${circ}`}
                                  strokeDashoffset={-(seg.start / 100) * circ} />
                              ))}
                              <circle cx="50" cy="50" r="34.5" fill={darkMode ? '#0f172a' : 'rgba(255,255,255,0.4)'} />
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <span className={`text-[10px] ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>Total</span>
                              <span className={`text-sm font-bold ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                                {privacyMode ? '•••' : formatCurrency(Math.round(donutTotal))}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </button>

                  {/* Budget Progress Bars */}
                  <button
                    onClick={() => handleTabChange('budgetGoals')}
                    className={`text-left rounded-xl p-5 transition-all hover:scale-[1.02] flex flex-col justify-start ${
                      darkMode
                        ? 'bg-slate-800/50 hover:bg-slate-800/70 border border-slate-700/30 shadow-sm'
                        : 'bg-white/70 hover:bg-white backdrop-blur-xl border border-transparent shadow-[0_32px_32px_rgba(107,56,212,0.04)] hover:shadow-[0_32px_32px_rgba(107,56,212,0.08)]'
                    }`}
                  >
                    <h3 className={`text-sm font-semibold mb-3 ${darkMode ? 'text-white' : 'text-slate-900'}`}>Budget Overview</h3>
                    {(() => {
                      const top5 = budgetUtilization.slice(0, 5);
                      if (top5.length === 0) {
                        return (
                          <div className={`text-center py-6 text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                            No budget categories yet
                          </div>
                        );
                      }
                      const totalRemaining = budgetUtilization.reduce((s, b) => s + b.remaining, 0);
                      return (
                        <>
                          <div className="space-y-2.5">
                            {top5.map((cat, i) => {
                              const pct = Math.min(cat.percentage, 100);
                              const over = cat.percentage > 100;
                              return (
                                <div key={i}>
                                  <div className="flex justify-between text-[11px] mb-0.5">
                                    <span className={`truncate ${darkMode ? 'text-slate-300' : 'text-slate-600'}`}>{cat.category}</span>
                                    <span className={`ml-2 font-medium flex-shrink-0 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                      {privacyMode ? '•••' : `${formatCurrency(cat.spent)} / ${formatCurrency(cat.budgeted)}`}
                                    </span>
                                  </div>
                                  <div className={`w-full h-1.5 rounded-full ${darkMode ? 'bg-white/10' : 'bg-white/40'}`}>
                                    <div
                                      className={`h-1.5 rounded-full transition-all ${over ? 'bg-red-500' : 'bg-emerald-500'}`}
                                      style={{ width: `${pct}%` }}
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          <div className={`mt-3 pt-2 border-t text-xs flex justify-between ${
                            darkMode ? 'border-white/10 text-slate-400' : 'border-slate-100 text-slate-500'
                          }`}>
                            <span>Remaining</span>
                            <span className={`font-medium ${totalRemaining >= 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : 'text-red-500'}`}>
                              {privacyMode ? '••••' : formatCurrency(totalRemaining)}
                            </span>
                          </div>
                        </>
                      );
                    })()}
                  </button>

                  <FearGreedWidget variant="page" />
                </div>}

                {/* Quick-nav Section Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  <button
                    data-tour="accounts-card"
                    onClick={() => handleTabChange('accounts')}
                    className={`text-left rounded-xl p-5 transition-all hover:scale-[1.02] flex flex-col justify-start ${
                      darkMode
                        ? 'bg-slate-800/50 hover:bg-slate-800/70 border border-slate-700/30 shadow-sm'
                        : 'bg-white/70 hover:bg-white backdrop-blur-xl border border-transparent shadow-[0_32px_32px_rgba(107,56,212,0.04)] hover:shadow-[0_32px_32px_rgba(107,56,212,0.08)]'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-violet-500/15' : 'bg-violet-500/10'}`}>
                        <Landmark size={20} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
                      </div>
                      <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Accounts</h3>
                    </div>
                    <div className={`space-y-1 text-sm w-full ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <div className="flex justify-between">
                        <span>Banking ({bankingData.length})</span>
                        <span className={`font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          {formatCurrency(totalBanking, privacyMode)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Credit Cards ({creditCards.length})</span>
                        <span className={`font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                          {formatCurrency(totalCreditUsed, privacyMode)}
                        </span>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleTabChange('investments')}
                    className={`text-left rounded-xl p-5 transition-all hover:scale-[1.02] flex flex-col justify-start ${
                      darkMode
                        ? 'bg-slate-800/50 hover:bg-slate-800/70 border border-slate-700/30 shadow-sm'
                        : 'bg-white/70 hover:bg-white backdrop-blur-xl border border-transparent shadow-[0_32px_32px_rgba(107,56,212,0.04)] hover:shadow-[0_32px_32px_rgba(107,56,212,0.08)]'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-violet-500/15' : 'bg-violet-500/10'}`}>
                        <TrendingUp size={20} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
                      </div>
                      <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Investments</h3>
                    </div>
                    <div className={`space-y-1 text-sm w-full ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <div className="flex justify-between">
                        <span>Portfolio ({investments.length})</span>
                        <span className={`font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          {formatCurrency(totalInvestmentValue, privacyMode)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>401k ({retirement401k.length})</span>
                        <span className={`font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          {formatCurrency(total401k, privacyMode)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Private Equity ({privateEquityHoldings.length})</span>
                        <span className={`font-medium ${darkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                          {formatCurrency(totalPrivateEquity, privacyMode)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Crypto ({cryptoHoldings.length})</span>
                        <span className={`font-medium ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>
                          {formatCurrency(totalCrypto, privacyMode)}
                        </span>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleTabChange('budgetGoals')}
                    className={`text-left rounded-xl p-5 transition-all hover:scale-[1.02] flex flex-col justify-start ${
                      darkMode
                        ? 'bg-slate-800/50 hover:bg-slate-800/70 border border-slate-700/30 shadow-sm'
                        : 'bg-white/70 hover:bg-white backdrop-blur-xl border border-transparent shadow-[0_32px_32px_rgba(107,56,212,0.04)] hover:shadow-[0_32px_32px_rgba(107,56,212,0.08)]'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-violet-500/15' : 'bg-violet-500/10'}`}>
                        <Target size={20} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
                      </div>
                      <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Budget & Goals</h3>
                    </div>
                    <div className={`space-y-1 text-sm w-full ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <div className="flex justify-between">
                        <span>Budget Remaining</span>
                        <span className={`font-medium ${budgetRemaining >= 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : 'text-red-500'}`}>
                          {formatCurrency(budgetRemaining, privacyMode)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Goals ({goals.length})</span>
                        <span className={`font-medium ${darkMode ? 'text-violet-400' : 'text-violet-600'}`}>
                          {privacyMode ? '••••••' : `${goalsProgress}%`}
                        </span>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleTabChange('assetsDebts')}
                    className={`text-left rounded-xl p-5 transition-all hover:scale-[1.02] flex flex-col justify-start ${
                      darkMode
                        ? 'bg-slate-800/50 hover:bg-slate-800/70 border border-slate-700/30 shadow-sm'
                        : 'bg-white/70 hover:bg-white backdrop-blur-xl border border-transparent shadow-[0_32px_32px_rgba(107,56,212,0.04)] hover:shadow-[0_32px_32px_rgba(107,56,212,0.08)]'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-violet-500/15' : 'bg-violet-500/10'}`}>
                        <Home size={20} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
                      </div>
                      <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Assets & Debts</h3>
                    </div>
                    <div className={`space-y-1 text-sm w-full ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      <div className="flex justify-between">
                        <span>Assets ({assets.length})</span>
                        <span className={`font-medium ${darkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                          {formatCurrency(totalAssets, privacyMode)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Liabilities ({liabilities.length})</span>
                        <span className={`font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                          {formatCurrency(totalLiabilities, privacyMode)}
                        </span>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleTabChange('charts')}
                    className={`text-left rounded-xl p-5 transition-all hover:scale-[1.02] flex flex-col justify-start ${
                      darkMode
                        ? 'bg-slate-800/50 hover:bg-slate-800/70 border border-slate-700/30 shadow-sm'
                        : 'bg-white/70 hover:bg-white backdrop-blur-xl border border-transparent shadow-[0_32px_32px_rgba(107,56,212,0.04)] hover:shadow-[0_32px_32px_rgba(107,56,212,0.08)]'
                    }`}
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`p-2 rounded-lg ${darkMode ? 'bg-violet-500/15' : 'bg-violet-500/10'}`}>
                        <BarChart3 size={20} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
                      </div>
                      <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Charts & Analytics</h3>
                    </div>
                    <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      Net worth history, asset allocation, investment returns, and more
                    </p>
                  </button>

                  <MilestonesWidget />
                </div>
              </>
            )}

            {/* Accounts Section */}
            {activeTab === 'accounts' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>Total Cash</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(totalBanking, privacyMode)}
                    </p>
                  </div>
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>Credit Used</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(totalCreditUsed, privacyMode)}
                    </p>
                  </div>
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-teal-300' : 'text-teal-600'}`}>HSA</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(totalHSA, privacyMode)}
                    </p>
                  </div>
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>Utilization</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {privacyMode ? '••••••' : `${creditUtilization}%`}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {isWidgetVisible('banking') && <BankingWidget
                    variant="page"
                    onAdd={() => setActiveModal('bankAccount')}
                    onUpgradeClick={() => openUpgradeModal('bankAccounts')}
                  />}
                  {isWidgetVisible('creditCards') && <CreditCardsWidget variant="page" onAdd={() => setActiveModal('creditCard')} />}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {isWidgetVisible('hsa') && <HSAWidget variant="page" onAdd={() => setActiveModal('hsa')} />}
                  {isWidgetVisible('creditScores') && <CreditScoresWidget variant="page" onAdd={() => setActiveModal('creditScore')} />}
                </div>
              </div>
            )}

            {/* Investments Section */}
            {activeTab === 'investments' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 md:gap-4">
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>Portfolio</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(totalInvestmentValue + total401k + totalPrivateEquity + totalCrypto, privacyMode)}
                    </p>
                  </div>
                  {isWidgetVisible('retirement401k') && <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-violet-300' : 'text-violet-600'}`}>401k</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(total401k, privacyMode)}
                    </p>
                  </div>}
                  {isWidgetVisible('privateEquity') && <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>Private Equity</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(totalPrivateEquity, privacyMode)}
                    </p>
                  </div>}
                  {isWidgetVisible('crypto') && <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-amber-300' : 'text-amber-600'}`}>Crypto</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(totalCrypto, privacyMode)}
                    </p>
                  </div>}
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? totalInvestmentGain >= 0
                        ? 'bg-slate-800/50 border border-slate-700/30'
                        : 'bg-slate-800/50 border border-slate-700/30'
                      : totalInvestmentGain >= 0
                        ? 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                        : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${
                      totalInvestmentGain >= 0
                        ? (darkMode ? 'text-emerald-300' : 'text-emerald-600')
                        : (darkMode ? 'text-red-300' : 'text-red-600')
                    }`}>Total Gain/Loss</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {privacyMode ? '••••••' : `${totalInvestmentGain >= 0 ? '+' : ''}${formatCurrency(totalInvestmentGain)}`}
                    </p>
                  </div>
                </div>
                {isWidgetVisible('investments') && <InvestmentsWidget
                  variant="page"
                  onAdd={() => setActiveModal('investment')}
                  onUpgradeClick={() => openUpgradeModal('investments')}
                  onImportCSV={() => setShowInvestmentCSVImport(true)}
                />}
                {isWidgetVisible('retirement401k') && <Retirement401kWidget
                  variant="page"
                  onAdd={() => setActiveModal('retirement401k')}
                  onUpgradeClick={() => openUpgradeModal('retirement401kHoldings')}
                  onImportCSV={() => setShowCSVImport(true)}
                />}
                {isWidgetVisible('privateEquity') && <PrivateEquityWidget
                  variant="page"
                  onAdd={() => setActiveModal('privateEquity')}
                  onUpgradeClick={() => openUpgradeModal('privateEquityHoldings')}
                />}
                {isWidgetVisible('crypto') && <CryptoWidget
                  variant="page"
                  onAdd={() => setActiveModal('crypto')}
                  onUpgradeClick={() => openUpgradeModal('cryptoHoldings')}
                />}
                {/* Mini allocation chart preview */}
                {chartData.length > 0 && (
                  <button
                    onClick={() => openChart('allocation')}
                    className={`w-full rounded-xl p-4 flex items-center gap-4 transition-all ${
                      darkMode
                        ? 'bg-slate-800/30 border border-slate-700/30 hover:bg-slate-800/50'
                        : 'bg-white border border-slate-100 hover:bg-slate-50 shadow-[0_8px_30px_rgb(0,0,0,0.04)]'
                    }`}
                  >
                    <svg viewBox="0 0 100 100" className="w-16 h-16 flex-shrink-0 -rotate-90">
                      {chartData.map((item, idx) => {
                        const circumference = 2 * Math.PI * 40;
                        const dashArray = (item.percentage / 100) * circumference;
                        const dashOffset = -(item.startPercentage / 100) * circumference;
                        return (
                          <circle key={idx} cx="50" cy="50" r="40" fill="none" stroke={item.color} strokeWidth="18"
                            strokeDasharray={`${dashArray} ${circumference}`} strokeDashoffset={dashOffset} />
                        );
                      })}
                      <circle cx="50" cy="50" r="31" fill={darkMode ? '#0f172a' : '#ffffff'} />
                    </svg>
                    <div className="text-left">
                      <p className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-slate-900'}`}>Asset Allocation</p>
                      <p className={`text-xs ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>Tap to view full chart</p>
                    </div>
                    <svg className={`w-4 h-4 ml-auto ${darkMode ? 'text-slate-500' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                )}
              </div>
            )}

            {/* Budget & Goals Section */}
            {activeTab === 'budgetGoals' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-blue-300' : 'text-blue-600'}`}>Budgeted</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(totalBudgeted, privacyMode)}
                    </p>
                  </div>
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>Spent</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(totalSpent, privacyMode)}
                    </p>
                  </div>
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? budgetRemaining >= 0
                        ? 'bg-slate-800/50 border border-slate-700/30'
                        : 'bg-slate-800/50 border border-slate-700/30'
                      : budgetRemaining >= 0
                        ? 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                        : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${
                      budgetRemaining >= 0
                        ? (darkMode ? 'text-emerald-300' : 'text-emerald-600')
                        : (darkMode ? 'text-red-300' : 'text-red-600')
                    }`}>Available</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(budgetRemaining, privacyMode)}
                    </p>
                  </div>
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-amber-300' : 'text-amber-600'}`}>Goals</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {privacyMode ? '••••••' : `${goalsProgress}%`}
                    </p>
                  </div>
                </div>
                {isWidgetVisible('budget') && <BudgetWidget variant="page" onAdd={() => setActiveModal('budgetCategory')} />}
                {isWidgetVisible('goals') && <GoalsWidget variant="page" onAdd={() => setActiveModal('goal')} />}
              </div>
            )}

            {/* Assets & Debts Section */}
            {activeTab === 'assetsDebts' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-emerald-300' : 'text-emerald-600'}`}>Total Assets</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(totalAssets, privacyMode)}
                    </p>
                  </div>
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-red-300' : 'text-red-600'}`}>Total Liabilities</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(totalLiabilities + totalCreditUsed, privacyMode)}
                    </p>
                  </div>
                  <div className={`rounded-xl p-4 ${
                    darkMode
                      ? 'bg-slate-800/50 border border-slate-700/30'
                      : 'bg-white/70 backdrop-blur-xl shadow-[0_32px_32px_rgba(107,56,212,0.04)]'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-violet-300' : 'text-violet-600'}`}>Net Worth</p>
                    <p className={`text-sm md:text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                      {formatCurrency(netWorth, privacyMode)}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {isWidgetVisible('assets') && <AssetsWidget variant="page" onAdd={() => setActiveModal('asset')} />}
                  {isWidgetVisible('liabilities') && <LiabilitiesWidget variant="page" onAdd={() => setActiveModal('liability')} />}
                </div>
              </div>
            )}

            {overlayChart && (
              <ChartsWidget
                variant="page"
                modalOnly
                defaultTab={overlayChart}
                onClose={() => setOverlayChart(undefined)}
                onUpgradeClick={() => setShowUpgradeModal(true)}
              />
            )}

            {/* Charts Section */}
            {activeTab === 'charts' && (
              <ChartsWidget variant="page" onUpgradeClick={() => setShowUpgradeModal(true)} defaultTab={chartsDefaultTab} onDefaultTabConsumed={() => setChartsDefaultTab(undefined)} />
            )}

            {/* Retirement Section */}
            {activeTab === 'retirement' && (
              <div className="space-y-6">
                <RetirementProjection variant="page" onUpgradeClick={() => setShowUpgradeModal(true)} />
                <p className={`text-[11px] text-center -mb-4 ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                  This projection is for planning and educational purposes only. Results are estimates based on the inputs provided and do not constitute financial advice or guaranteed projections.
                </p>
              </div>
            )}

            {/* Calculators Section */}
            {activeTab === 'calculators' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <CompoundInterestCalculator variant="page" />
                  <FIRECalculator variant="page" />
                  <RetirementCalculator variant="page" />
                  <DebtPayoffCalculator variant="page" />
                </div>
                <p className={`text-[11px] text-center -mb-4 ${darkMode ? 'text-slate-600' : 'text-slate-400'}`}>
                  These calculators are for planning and educational purposes only. Results are estimates based on the inputs provided and do not constitute financial advice or guaranteed projections.
                </p>
              </div>
            )}
          </div>

        </main>
      </div>

      {/* Footer - centered on full viewport */}
      <div className={`hidden md:flex justify-center py-3 text-xs ${
        darkMode ? 'text-slate-600' : 'text-slate-400'
      }`}>
        <span style={{ transform: `translateX(-${sidebarCollapsed ? 32 : 112}px)` }}>
          &copy; {new Date().getFullYear()} TallyUp Finance &middot; Your money. Your data. Your control.
        </span>
      </div>

      {/* Mobile Bottom Nav — floating pill with carousel paging + scroll-shrink */}
      <motion.nav
        className="md:hidden fixed left-1/2 z-40 pointer-events-none w-[84vw] max-w-[360px]"
        style={{
          bottom: `calc(${bottomNavInsetPx}px + env(safe-area-inset-bottom))`,
          transformOrigin: 'center bottom',
          x: '-50%',
          scale: bottomNavScale,
          y: bottomNavY,
          transition: 'bottom 200ms ease-out',
        }}
      >
        <div
          className={`pointer-events-auto overflow-hidden rounded-full backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.18)] ${
            darkMode
              ? 'bg-slate-900/65 border border-slate-700/50'
              : 'bg-white/65 border border-slate-200/70'
          }`}
        >
          <motion.div
            className="flex"
            style={{ width: `${bottomNavTotalPages * 100}%` }}
            animate={{ x: `-${bottomNavPage * (100 / bottomNavTotalPages)}%` }}
            transition={{ type: 'spring', damping: 28, stiffness: 240 }}
          >
            {Array.from({ length: bottomNavTotalPages }).map((_, pageIdx) => {
              const pageTabs = DASHBOARD_TABS.slice(
                pageIdx * BOTTOM_NAV_PAGE_SIZE,
                (pageIdx + 1) * BOTTOM_NAV_PAGE_SIZE,
              );
              const isFirstPage = pageIdx === 0;
              const isLastPage = pageIdx === bottomNavTotalPages - 1;
              return (
                <div
                  key={pageIdx}
                  className="flex items-center px-1 py-2.5"
                  style={{ width: `${100 / bottomNavTotalPages}%` }}
                >
                  {!isFirstPage && (
                    <button
                      onClick={() => setBottomNavPage(pageIdx - 1)}
                      aria-label="Previous tabs"
                      className={`flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 transition-colors ${
                        darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-700'
                      }`}
                    >
                      <ChevronLeft size={22} />
                    </button>
                  )}
                  {pageTabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.key;
                    return (
                      <button
                        key={tab.key}
                        onClick={() => handleTabChange(tab.key)}
                        className={`flex items-center justify-center h-10 rounded-full flex-1 min-w-0 transition-colors ${
                          isActive
                            ? darkMode
                              ? 'text-violet-400'
                              : 'text-[#6b38d4]'
                            : darkMode
                              ? 'text-slate-500'
                              : 'text-slate-400'
                        }`}
                      >
                        <div className="relative">
                          <Icon size={24} />
                        </div>
                      </button>
                    );
                  })}
                  {!isLastPage && (
                    <button
                      onClick={() => setBottomNavPage(pageIdx + 1)}
                      aria-label="More tabs"
                      className={`flex items-center justify-center w-10 h-10 rounded-full flex-shrink-0 transition-colors ${
                        darkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-700'
                      }`}
                    >
                      <Plus size={22} />
                    </button>
                  )}
                  {Array.from({
                    length: Math.max(0, BOTTOM_NAV_PAGE_SIZE - pageTabs.length),
                  }).map((_, i) => (
                    <div key={`spacer-${i}`} aria-hidden className="flex-1 h-10" />
                  ))}
                </div>
              );
            })}
          </motion.div>
        </div>
      </motion.nav>
    </div>
  );
};
